'use client';
import { useTheme } from '@mui/material';
import React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { StrapiBackgroundOptions, StrapiDisplayOptions } from '@/lib/DisplayOptionsHelper';
import { useSpecialtyCalculators } from '@/lib/hooks/useSpecialtyCalculators';
import {
  ComponentBlocksLoanCalculators,
  Enum_Componentshareddisplayoptions_Contentswidth,
} from '@/types.d';

import {
  CalculatorInputs,
  CalculatorLayout,
  CalculatorOutputs,
  OutputField,
  OutputGraphic,
  Styled2ColGrid,
  StyledColumn,
  StyledTextSlider,
} from './components';

type BusinessOverdraftFormValues = {
  overdraftLimit: number;
  amountDrawn: number;
  annualRate: number;
  daysDrawn: number;
};

const BusinessOverdraft = ({ block }: { block: ComponentBlocksLoanCalculators }) => {
  const theme = useTheme();
  const { calculateBusinessOverdraft } = useSpecialtyCalculators();

  const methods = useForm<BusinessOverdraftFormValues>({
    defaultValues: {
      overdraftLimit: 100000,
      amountDrawn: 50000,
      annualRate: 8.5,
      daysDrawn: 30,
    },
  });
  const { control, watch } = methods;
  const values = watch();

  const result = calculateBusinessOverdraft({
    overdraftLimit: values.overdraftLimit || 0,
    amountDrawn: values.amountDrawn || 0,
    annualRate: values.annualRate || 0,
    daysDrawn: values.daysDrawn || 0,
  });

  return (
    <FormProvider {...methods}>
      <CalculatorLayout
        title={block?.CustomCalculatorTitle ?? undefined}
        blurb={block?.CalculatorBlurb}
        backgroundOptions={(block?.CalculatorBackground ?? undefined) as StrapiBackgroundOptions}
        displayOptions={
          {
            ...block?.CalculatorDisplayOptions,
            ContentsWidth:
              block?.CalculatorDisplayOptions?.ContentsWidth ??
              Enum_Componentshareddisplayoptions_Contentswidth.Wide,
            PaddingTop: block?.CalculatorDisplayOptions?.PaddingTop ?? 'md',
            PaddingBottom: block?.CalculatorDisplayOptions?.PaddingBottom ?? 'md',
            PaddingInline: block?.CalculatorDisplayOptions?.PaddingInline ?? 'sm',
          } as StrapiDisplayOptions
        }
        rowLayout={false}
        dataComponent="BusinessOverdraft"
      >
        <CalculatorInputs title={block?.CustomInputsTitle ?? 'Enter overdraft details'}>
          <Controller
            name="overdraftLimit"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Overdraft Facility Limit"
                prefix="$"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="amountDrawn"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Amount Drawn"
                prefix="$"
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="annualRate"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Annual Interest Rate"
                suffix="%"
                value={field.value}
                defaultMax={25}
                step={0.1}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            name="daysDrawn"
            control={control}
            render={({ field }) => (
              <StyledTextSlider
                label="Days Drawn"
                suffix=" days"
                value={field.value}
                minValue={1}
                maxValue={365}
                defaultMax={365}
                onChange={field.onChange}
              />
            )}
          />
        </CalculatorInputs>

        <CalculatorOutputs title={block?.CustomOutputsTitle ?? 'Overdraft Cost Summary'}>
          <Styled2ColGrid>
            <OutputGraphic
              data={[
                {
                  label: 'Amount Drawn',
                  value: result.amountDrawnRaw,
                  color: theme.palette.brand.primary,
                },
                {
                  label: 'Available Headroom',
                  value: result.availableHeadroomRaw,
                  color: theme.palette.brand.secondary,
                },
              ]}
            />
            <StyledColumn>
              <OutputField label="Interest for Period" value={`$${result.interestForPeriod}`} />
              <OutputField label="Monthly Interest" value={`$${result.monthlyInterest}`} />
            </StyledColumn>
          </Styled2ColGrid>
          <Styled2ColGrid>
            <OutputField label="Annual Interest Cost" value={`$${result.annualInterest}`} />
            <OutputField
              label="Available Headroom"
              value={`$${result.availableHeadroom}`}
              highlight
            />
          </Styled2ColGrid>
        </CalculatorOutputs>
      </CalculatorLayout>
    </FormProvider>
  );
};

export default BusinessOverdraft;
